<template>
  <v-container
    fluid
    class="login-shell ma-0 pa-0 d-flex align-center justify-center fill-height"
  >
    <Snackbar :SnackbarComponent="SnackbarComponent" />
    <v-sheet class="login-frame" color="transparent" elevation="0" width="100%">
      <v-card
        class="login-panel pa-8"
        elevation="0"
        rounded="xl"
        color="transparent"
      >
        <v-sheet
          class="login-brand d-flex justify-center"
          color="transparent"
          elevation="0"
        >
          <v-img
            src="@/assets/StichhIcon.png"
            alt="Stichh"
            max-width="70"
            class="login-logo"
            contain
          />
        </v-sheet>

        <v-sheet
          v-if="loginSuccess"
          class="login-success"
          color="transparent"
          elevation="0"
        >
          <Vue3Lottie
            :animationData="LogInSuccessful"
            :height="160"
            :width="160"
            autoplay
            :loop="false"
          />
          <v-card-title
            class="login-success-title justify-center text-wrap pa-0 pt-2"
          >
            Signed in
          </v-card-title>
          <v-card-text class="login-success-sub pa-0 pt-1 pb-0">
            Taking you to your workspace…
          </v-card-text>
        </v-sheet>

        <v-window v-else v-model="signinwindow" class="login-window mt-n4">
          <v-window-item :value="1">
            <v-sheet class="login-header" color="transparent" elevation="0">
              <v-card-title class="login-title justify-center text-wrap pa-0">
                Sign in
              </v-card-title>
              <v-card-text class="login-lede pa-0 pt-2 pb-0">
                Enter your email. We’ll send a one-time code.
              </v-card-text>
            </v-sheet>
            <v-form
              ref="emailform"
              class="login-form"
              @submit.prevent="ValidateEmailFormMethod"
            >
              <v-text-field
                v-model="email_id"
                type="email"
                autocomplete="email"
                variant="outlined"
                density="comfortable"
                hide-details="auto"
                class="login-field"
                placeholder="you@company.com"
                :rules="[
                  (v) => !!v || 'Required',
                  (v) =>
                    /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(
                      v,
                    ) || 'Enter a valid email',
                ]"
              />
            </v-form>
            <v-btn
              block
              size="large"
              rounded="pill"
              class="login-submit mt-6"
              :loading="loading"
              @click="ValidateEmailFormMethod"
            >
              Continue
            </v-btn>
          </v-window-item>

          <v-window-item :value="2">
            <v-sheet class="login-header" color="transparent" elevation="0">
              <v-card-title class="login-title justify-center text-wrap pa-0">
                Check your inbox
              </v-card-title>
              <v-card-text class="login-lede pa-0 pt-2 pb-0">
                Enter the 6-digit code we sent to
              </v-card-text>
              <v-card-text class="login-email pa-0 pt-1 pb-0">
                {{ email }}
              </v-card-text>
            </v-sheet>
            <v-form
              ref="otpform"
              class="login-form"
              @submit.prevent="ValidateOTPFormMethod"
            >
              <v-otp-input
                ref="otpInput"
                v-model="sign_in_otp"
                class="login-otp"
                :rules="[
                  (v) => !!v || 'Required',
                  (v) => v.length === 6 || 'Invalid code',
                ]"
                :length="6"
                @finish="ValidateOTPFormMethod"
              />
            </v-form>
            <v-sheet
              class="login-resend d-flex flex-wrap align-center justify-center"
              color="transparent"
              elevation="0"
            >
              <v-card-text
                v-if="resendTimer > 0"
                class="login-resend-hint pa-0"
              >
                Resend in {{ resendTimer }}s
              </v-card-text>
              <template v-else>
                <v-card-text class="login-resend-hint pa-0">
                  Didn’t get it?
                </v-card-text>
                <v-btn
                  variant="text"
                  size="small"
                  class="login-resend-btn"
                  :disabled="resendTimer > 0"
                  @click="ValidateEmailFormMethod"
                >
                  Resend code
                </v-btn>
              </template>
            </v-sheet>
            <v-btn
              block
              size="large"
              rounded="pill"
              class="login-submit mt-6"
              :loading="loading"
              @click="ValidateOTPFormMethod"
            >
              Sign in
            </v-btn>
          </v-window-item>
        </v-window>
      </v-card>
      <v-card-text class="login-footnote pa-0 mt-4 text-center bg-transparent">
        Secure sign-in with email verification
      </v-card-text>
    </v-sheet>
  </v-container>
</template>

<script>
import { signIn, signOut, confirmSignIn } from "aws-amplify/auth";
import { Vue3Lottie } from "vue3-lottie";

import Snackbar from "@/components/Snackbar.vue";
import LoginAnimation from "@/assets/LoginAnimation.json";
import LogInSuccessful from "@/assets/LogInSuccessful.json";

export default {
  components: { Snackbar, Vue3Lottie },

  data: () => ({
    LoginAnimation,
    LogInSuccessful,

    email_id: "",
    sign_in_otp: "",

    signinwindow: 1,
    resendTimer: 0,

    loading: false,
    loginSuccess: false,

    resendInterval: null,

    SnackbarComponent: {},
  }),

  computed: {
    email() {
      return this.$store.getters.getEmail;
    },
  },

  methods: {
    async ValidateEmailFormMethod() {
      if (this.signinwindow === 2) {
        const addr = this.email_id || this.email;
        if (!addr) return;
        this.email_id = addr;
        await this.signInMethod();
        return;
      }
      const { valid } = await this.$refs.emailform.validate();
      if (valid && this.email_id) {
        this.$store.commit("setEmail", this.email_id);
        this.signInMethod();
      }
    },
    async signInMethod() {
      this.loading = true;
      try {
        let result = await signIn({
          username: this.email_id,
          options: {
            authFlowType: "CUSTOM_WITHOUT_SRP",
          },
        });
        if (
          result.nextStep.signInStep === "CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE"
        ) {
          if (
            result.nextStep.challengeParameters &&
            result.nextStep.challengeParameters.email
          ) {
            this.$store.commit(
              "setEmail",
              result.nextStep.challengeParameters.email,
            );
          }
          this.SnackbarComponent = {
            SnackbarVmodel: true,
            SnackbarColor: "green",
            SnackbarMessage: "OTP sent successfully!",
          };
          this.signinwindow = 2;
          this.startResendTimer();
          this.sign_in_otp = "";
          this.loading = false;
          this.$nextTick(() => {
            this.$refs.otpInput.$el.querySelector("input").focus();
          });
        }
      } catch (err) {
        this.loading = false;
        if (err.message === "There is already a signed in user.") {
          await signOut({ global: true });
          this.signInMethod();
          this.SnackbarComponent = {
            SnackbarVmodel: true,
            SnackbarColor: "red",
            SnackbarMessage: err.message,
          };
        }
        if (err.message === "Unrecognizable lambda output") {
          this.SnackbarComponent = {
            SnackbarVmodel: true,
            SnackbarColor: "red",
            SnackbarMessage: "Email ID Does not Exist!",
          };
        }
      }
    },
    async ValidateOTPFormMethod() {
      const { valid } = await this.$refs.otpform.validate();
      if (valid) {
        this.VerifyOTPMethod();
      }
    },
    async VerifyOTPMethod() {
      this.loading = true;
      try {
        const otpResult = await confirmSignIn({
          challengeResponse: this.sign_in_otp,
        });
        this.loading = false;
        if (otpResult.isSignedIn && otpResult.nextStep.signInStep === "DONE") {
          this.loginSuccess = true;
          setTimeout(() => {
            this.$router.push("Dashboard");
          }, 2000);
        } else {
          this.SnackbarComponent = {
            SnackbarVmodel: true,
            SnackbarColor: "red",
            SnackbarMessage: "Invalid OTP!",
          };
        }
      } catch (err) {
        this.loading = false;
        this.SnackbarComponent = {
          SnackbarVmodel: true,
          SnackbarColor: "red",
          SnackbarMessage: "Oops, wrong OTP, try again!",
        };
        if (err.message === "Invalid session for the user.") {
          this.SnackbarComponent = {
            SnackbarVmodel: true,
            SnackbarColor: "red",
            SnackbarMessage: "Session Timed Out, Refresh and Try Again!",
          };
        }
      }
    },
    startResendTimer() {
      this.resendTimer = 30;

      if (this.resendInterval) {
        clearInterval(this.resendInterval);
      }

      this.resendInterval = setInterval(() => {
        if (this.resendTimer > 0) {
          this.resendTimer--;
        } else {
          clearInterval(this.resendInterval);
          this.resendInterval = null;
        }
      }, 1000);
    },
  },
};
</script>
